import { motion } from "framer-motion";
import dayjs from "dayjs";
import { PendingActivity } from "./PendingActivities";
import ActivityItem from "./ActivityItem";
import EmptyState from "./EmptyState";
import RescheduleRequest from "components/reschedule-request/RescheduleRequest";
import CancelBookingRequest from "components/cancel-booking-request/CancelBookingRequest";
import ManageBookingActions from "components/manage-booking-actions/ManageBookingActions";
import { faCalendarAlt, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatLeft, canRescheduleAccepted } from "./PendingActivities";
import { canCancelBookingFromApplicationDate } from "helpers/booking-cancellation-helper";
import { buildCancelBookingRequestData } from "helpers/cancel-booking-request-helper";
import "./PendingActivities.scss";

const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

interface BookingsTabProps {
  bookings: PendingActivity[];
  loading: boolean;
  showCreatorInfo?: boolean;
  onReschedule?: (activity: PendingActivity) => void;
  onCancelSuccess?: (activity: PendingActivity) => void;
}

const BookingsTab: React.FC<BookingsTabProps> = ({
  bookings,
  loading,
  showCreatorInfo,
  onCancelSuccess,
}) => {
  if (bookings.length === 0 && !loading) {
    return (
      <EmptyState
        icon={faCalendarAlt}
        title="No Upcoming Bookings"
        description="This creator doesn't have any confirmed bookings yet. Once they accept invitations or you confirm their applications, they'll appear here."
        actions={
          <>
            <Button
              type="primary"
              onClick={() => {
                /* implement send invitation flow if needed */
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
              Send Invitation
            </Button>
            <Button
              onClick={() => {
                /* implement add to campaign flow if needed */
              }}
            >
              + Add to Campaign
            </Button>
          </>
        }
      />
    );
  }

  return (
    <motion.div
      className="activity-list bookings-list"
      variants={listVariants}
      initial="hidden"
      animate="visible"
    >
      {bookings.map((booking) => {
        const status = String(booking?.tabStatus || "").toLowerCase();
        const isAccepted = status === "accepted";
        const bookingId = booking.bookingId ?? "";
        const collabId = booking.collabId ?? "";
        const hasBookingIdentifiers = Boolean(bookingId && collabId);
        const canRescheduleNow = canRescheduleAccepted(booking);
        const canCancelNow = canCancelBookingFromApplicationDate(
          booking.applicationDate,
        );
        const showManageActions =
          isAccepted &&
          hasBookingIdentifiers &&
          (canRescheduleNow || canCancelNow);

        return (
          <ActivityItem
            key={booking.id}
            activity={booking}
            showCreatorInfo={showCreatorInfo}
            type="booking"
            renderDateTime={() => (
              <div className="booking-datetime">
                <div className="booking-date">
                  {booking.applicationDate
                    ? dayjs
                        .utc(booking.applicationDate?.date)
                        .format("MMMM D, YYYY")
                    : formatLeft((booking as any).details)}
                </div>
                <div className="booking-time">
                  {booking.applicationDate?.hour || "-"}
                </div>
              </div>
            )}
            renderActions={() =>
              showManageActions ? (
                <ManageBookingActions
                  renderReschedule={(closeDropdown) => (
                    <RescheduleRequest
                      triggerVariant="menu_item"
                      onTriggerClick={closeDropdown}
                      disabled={!canRescheduleNow}
                      influencer={{
                        bookingId,
                        collabId,
                        tabStatus: (booking.tabStatus?.toLowerCase() ||
                          "accepted") as "accepted" | "pending" | "rejected" | "expired",
                        isPremium: booking.isPremium,
                        selfManagedCompensationEnabled:
                          booking.selfManagedCompensationEnabled,
                        cashCompAmount: booking.cashCompAmount ?? undefined,
                      }}
                    />
                  )}
                  renderCancel={(closeDropdown) => {
                    const cancelRequestProps = buildCancelBookingRequestData(
                      booking,
                      "pending_activities",
                    );

                    if (!cancelRequestProps) return null;

                    return (
                      <CancelBookingRequest
                        triggerVariant="menu_item"
                        onTriggerClick={closeDropdown}
                        disabled={!canCancelNow}
                        {...cancelRequestProps}
                        callback={() => onCancelSuccess?.(booking)}
                      />
                    );
                  }}
                />
              ) : null
            }
          />
        );
      })}
    </motion.div>
  );
};

export default BookingsTab;
